import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Handshake,
  Plus,
  Pencil,
  Trash2,
  Check,
  ArrowUpRight,
  ArrowDownLeft,
  Calendar,
  Loader2,
  AlertTriangle,
  FileCheck,
} from "lucide-react";
import { format, isPast, isToday } from "date-fns";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";
import { undertakingsApi } from "../../services/api";

/* ── Types ──────────────────────────────────────────────────────────── */

interface Undertaking {
  _id: string;
  direction: "given" | "received";
  description: string;
  counterpartyName: string | null;
  counterpartyContactId: any;
  dueDate: string | null;
  status: "outstanding" | "fulfilled" | "waived" | "breached";
  fulfilledAt: string | null;
  fulfilledBy: any;
  proofDocumentId: any;
  notes: string | null;
  createdAt: string;
}

interface UndertakingsTabProps {
  matterId: string;
}

/* ── Constants ─────────────────────────────────────────────────────── */

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  outstanding: { label: "Outstanding", bg: "bg-amber-100", text: "text-amber-800" },
  fulfilled: { label: "Fulfilled", bg: "bg-emerald-100", text: "text-emerald-800" },
  waived: { label: "Waived", bg: "bg-slate-100", text: "text-slate-600" },
  breached: { label: "Breached", bg: "bg-red-100", text: "text-red-800" },
};

const FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "outstanding", label: "Outstanding" },
  { value: "fulfilled", label: "Fulfilled" },
  { value: "given", label: "Given" },
  { value: "received", label: "Received" },
];

/* ── Component ─────────────────────────────────────────────────────── */

const UndertakingsTab: React.FC<UndertakingsTabProps> = ({ matterId }) => {
  const [undertakings, setUndertakings] = useState<Undertaking[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Undertaking | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    direction: "received" as "given" | "received",
    description: "",
    counterpartyName: "",
    dueDate: "",
    status: "outstanding",
    notes: "",
  });

  // ── Fetch ──
  const fetchUndertakings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await undertakingsApi.list({ matterId, limit: 200 });
      setUndertakings(res.data?.data || res.data || []);
    } catch {
      setUndertakings([]);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    fetchUndertakings();
  }, [fetchUndertakings]);

  // ── Filter ──
  const filtered = undertakings.filter((u) => {
    if (filter === "all") return true;
    if (filter === "outstanding") return u.status === "outstanding";
    if (filter === "fulfilled") return u.status === "fulfilled";
    if (filter === "given") return u.direction === "given";
    if (filter === "received") return u.direction === "received";
    return true;
  });

  const outstandingCount = undertakings.filter(
    (u) => u.status === "outstanding"
  ).length;

  // ── Handlers ──
  const openAdd = () => {
    setEditing(null);
    setForm({
      direction: "received",
      description: "",
      counterpartyName: "",
      dueDate: "",
      status: "outstanding",
      notes: "",
    });
    setShowModal(true);
  };

  const openEdit = (u: Undertaking) => {
    setEditing(u);
    setForm({
      direction: u.direction,
      description: u.description,
      counterpartyName: u.counterpartyName || "",
      dueDate: u.dueDate ? u.dueDate.slice(0, 10) : "",
      status: u.status,
      notes: u.notes || "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.description.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        matterId,
        counterpartyName: form.counterpartyName || null,
        dueDate: form.dueDate || null,
        notes: form.notes || null,
      };

      if (editing) {
        await undertakingsApi.update(editing._id, payload);
      } else {
        await undertakingsApi.create(payload);
      }

      setShowModal(false);
      fetchUndertakings();
    } catch (err) {
      console.error("Failed to save undertaking:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await undertakingsApi.remove(id);
      fetchUndertakings();
    } catch (err) {
      console.error("Failed to delete undertaking:", err);
    }
  };

  const handleMarkFulfilled = async (u: Undertaking) => {
    try {
      await undertakingsApi.update(u._id, { status: "fulfilled" });
      fetchUndertakings();
    } catch (err) {
      console.error("Failed to mark fulfilled:", err);
    }
  };

  const isDueSoon = (dueDate: string | null) => {
    if (!dueDate) return false;
    const d = new Date(dueDate);
    return isPast(d) || isToday(d);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Handshake className="h-5 w-5 text-amber-600" />
            <h3 className="font-semibold text-slate-800">Undertakings</h3>
            {outstandingCount > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                {outstandingCount} outstanding
              </span>
            )}
          </div>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            <Plus className="h-4 w-4" />
            Add Undertaking
          </button>
        </div>

        {/* Filter bar */}
        <div className="flex gap-1 mb-4">
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === opt.value
                  ? "bg-slate-800 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* List */}
        {filtered.length > 0 ? (
          <div className="space-y-2">
            <AnimatePresence>
              {filtered.map((u) => {
                const statusCfg = STATUS_CONFIG[u.status];
                const overdue =
                  u.status === "outstanding" && isDueSoon(u.dueDate);

                return (
                  <motion.div
                    key={u._id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    className={`p-4 rounded-lg border ${
                      overdue
                        ? "bg-red-50/50 border-red-200"
                        : "bg-slate-50 border-transparent"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        {/* Direction icon */}
                        <div
                          className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            u.direction === "given"
                              ? "bg-blue-100 text-blue-600"
                              : "bg-purple-100 text-purple-600"
                          }`}
                        >
                          {u.direction === "given" ? (
                            <ArrowUpRight className="h-4 w-4" />
                          ) : (
                            <ArrowDownLeft className="h-4 w-4" />
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-800">
                            {u.description}
                          </p>
                          <div className="flex flex-wrap items-center gap-3 mt-1">
                            <span className="text-xs text-slate-500">
                              {u.direction === "given"
                                ? "Given by us"
                                : "Received from them"}
                              {u.counterpartyName &&
                                ` — ${u.counterpartyName}`}
                            </span>
                            {u.dueDate && (
                              <span
                                className={`inline-flex items-center gap-1 text-xs ${
                                  overdue
                                    ? "text-red-600 font-medium"
                                    : "text-slate-500"
                                }`}
                              >
                                <Calendar className="h-3 w-3" />
                                {overdue && (
                                  <AlertTriangle className="h-3 w-3" />
                                )}
                                Due{" "}
                                {format(new Date(u.dueDate), "MMM d, yyyy")}
                              </span>
                            )}
                          </div>
                          {u.notes && (
                            <p className="text-xs text-slate-400 mt-1">
                              {u.notes}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.text}`}
                        >
                          {statusCfg.label}
                        </span>
                        {u.status === "outstanding" && (
                          <button
                            onClick={() => handleMarkFulfilled(u)}
                            className="p-1 text-emerald-500 hover:text-emerald-700 transition-colors"
                            title="Mark Fulfilled"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => openEdit(u)}
                          className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(u._id)}
                          className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        ) : (
          <div className="text-center py-8">
            <Handshake className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">
              {filter === "all"
                ? "No undertakings yet"
                : `No ${filter} undertakings`}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Track promises given to or received from other lawyers
            </p>
          </div>
        )}
      </div>

      {/* ── Add/Edit Modal ── */}
      <PremiumModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? "Edit Undertaking" : "Add Undertaking"}
        subtitle="A promise given to or received from another lawyer"
        icon={Handshake}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!form.description.trim() || saving}
              className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : editing ? (
                "Save Changes"
              ) : (
                "Add Undertaking"
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Direction toggle */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Direction
            </label>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setForm((prev) => ({ ...prev, direction: "given" }))
                }
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                  form.direction === "given"
                    ? "border-blue-300 bg-blue-50 text-blue-700"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <ArrowUpRight className="h-4 w-4" />
                Given by us
              </button>
              <button
                onClick={() =>
                  setForm((prev) => ({ ...prev, direction: "received" }))
                }
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                  form.direction === "received"
                    ? "border-purple-300 bg-purple-50 text-purple-700"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <ArrowDownLeft className="h-4 w-4" />
                Received from them
              </button>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description *
            </label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              rows={3}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              placeholder="e.g. Undertake to discharge existing mortgage within 60 days of closing"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Counterparty
              </label>
              <input
                type="text"
                value={form.counterpartyName}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    counterpartyName: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Other lawyer / firm name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, dueDate: e.target.value }))
                }
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Status (only shown when editing) */}
          {editing && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Status
              </label>
              <SearchableSelect
                options={[
                  { value: "outstanding", label: "Outstanding" },
                  { value: "fulfilled", label: "Fulfilled" },
                  { value: "waived", label: "Waived" },
                  { value: "breached", label: "Breached" },
                ]}
                value={form.status}
                onChange={(v) =>
                  setForm((prev) => ({ ...prev, status: v || "outstanding" }))
                }
                placeholder="Select status..."
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              rows={2}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              placeholder="Any additional notes..."
            />
          </div>
        </div>
      </PremiumModal>
    </div>
  );
};

export default UndertakingsTab;
